import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { getStopById } from 'Global/JourneyElements/Utils/getStopById';
import { appConfigStore } from 'Models/AppConfigStore';
import { GiftCertificateUsageEntity, RefundEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import {
	CancelledBookingSummaryDto,
	fetchCancelledBookingsForTransaction,
} from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { formatCurrency } from 'Util/_HumanWritten/CurrencyUtils';
import { creditTermLabel } from 'Util/_HumanWritten/ferryCreditTerm';
import { fetchGiftCertificateUsagesByTransactionId } from 'Util/_HumanWritten/FetchGiftCertificates';
import { formatShortDateYear, formatWeekdayDayMonthTime } from 'Util/_HumanWritten/DateUtils';
import { upperCaseFirst } from 'Util/StringUtils';
import { isNotNullOrUndefined, stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { VoucherTicket } from 'Views/Components/_HumanWritten/PurchaseGiftCertificate/VoucherTicket';
import Skeleton from 'Views/Components/_HumanWritten/Skeleton/Skeleton';

/*
	What a cancellation resolved to — who was cancelled, the refund or credit, and the cancelled trips.
	Shared by the booking-cancelled page and the cancellation wizard's result step (check-in), so the
	two cannot describe the same cancellation differently. Styles: `cancellation-result-content` in
	booking-success.scss.
*/

export interface CancellationResultData {
	refunds?: RefundEntity[];
	/** The zero-usage voucher issued as credit, if the cancellation was refunded to credit. */
	giftCertificateUsage?: GiftCertificateUsageEntity;
	bookings: CancelledBookingSummaryDto[];
	bookingsLoading: boolean;
	resolutionLoading: boolean;
}

/**
 * Loads everything the result shows for a cancellation transaction.
 * @param bookingId the booking the user cancelled from, floated to the top of the cancelled list
 */
export function useCancellationResult(transactionId: string, bookingId?: string): CancellationResultData {
	const [refunds, setRefunds] = useState<RefundEntity[]>();
	const [giftCertificateUsage, setGiftCertificateUsage] = useState<GiftCertificateUsageEntity | undefined>(undefined);
	const [bookings, setBookings] = useState<CancelledBookingSummaryDto[]>([]);
	const [bookingsLoading, setBookingsLoading] = useState(transactionId !== '');
	const [refundsLoading, setRefundsLoading] = useState(transactionId !== '');
	const [voucherLoading, setVoucherLoading] = useState(transactionId !== '');

	useEffect(() => {
		if (transactionId === '') return;

		// Fire all three in parallel — they're independent. Each section reveals its
		// content as soon as its own fetch resolves so the page doesn't feel stuck
		// behind the slowest call.
		fetchGiftCertificateUsagesByTransactionId(transactionId)
			.then(usages => setGiftCertificateUsage(usages.data.find(x => x.usedValue === 0)))
			.catch(() => setGiftCertificateUsage(undefined))
			.finally(() => setVoucherLoading(false));

		fetchRefunds(transactionId)
			.then(res => {
				if (res.length >= 1) setRefunds(res);
			})
			.catch(() => {
				/* leave refunds undefined */
			})
			.finally(() => setRefundsLoading(false));

		// One lightweight call returns every booking cancelled in this transaction
		// (departure + return etc.) with just the fields each row needs.
		fetchCancelledBookingsForTransaction(transactionId)
			.then(fetched => {
				if (fetched.length === 0) return;
				// Float the booking the user clicked Cancel on to the top.
				const ordered = bookingId
					? [...fetched.filter(b => b.id === bookingId), ...fetched.filter(b => b.id !== bookingId)]
					: fetched;
				setBookings(ordered);
			})
			.catch(() => {
				/* leave bookings empty */
			})
			.finally(() => setBookingsLoading(false));
	}, [transactionId, bookingId]);

	return {
		refunds,
		giftCertificateUsage,
		bookings,
		bookingsLoading,
		resolutionLoading: refundsLoading || voucherLoading,
	};
}

interface CancellationResultProps {
	result: CancellationResultData;
	userName?: string;
	isEventBooking: boolean;
	/**
	 * Whether each cancelled trip links to its booking page. Off in check-in, which must not send the
	 * operator out of check-in.
	 */
	linkTrips?: boolean;
}

export function CancellationResult({ result, userName, isEventBooking, linkTrips = true }: CancellationResultProps) {
	return (
		<>
			<CancelSpiel userName={userName} isEventBooking={isEventBooking} />
			<ResolutionSection
				refunds={result.refunds}
				giftCertificateUsage={result.giftCertificateUsage}
				redeemText={appConfigStore.voucherRedeemText}
				loading={result.resolutionLoading}
				isFerryBooking={!isEventBooking}
			/>
			<CancelledTripsSection bookings={result.bookings} loading={result.bookingsLoading} linkTrips={linkTrips} />
		</>
	);
}

// ─── Cancelled trips section (renders inside the main card under the spiel) ─

interface CancelledTripsSectionProps {
	bookings: CancelledBookingSummaryDto[];
	loading: boolean;
	linkTrips: boolean;
}

function CancelledTripsSection({ bookings, loading, linkTrips }: CancelledTripsSectionProps) {
	const rows = bookings.map(b => extractTripInfo(b)).filter((r): r is TripInfo => r !== null);

	if (loading) {
		return (
			<div className="cancelled-trips">
				<div className="cancel-resolution__heading">
					<span className="cancel-resolution__eyebrow">Cancelled booking</span>
				</div>
				<div className="cancelled-trips__list">
					<TripRowSkeleton />
				</div>
			</div>
		);
	}

	if (rows.length === 0) return null;

	return (
		<div className="cancelled-trips">
			<div className="cancel-resolution__heading">
				<span className="cancel-resolution__eyebrow">
					{rows.length > 1 ? 'Cancelled bookings' : 'Cancelled booking'}
				</span>
			</div>
			<div className="cancelled-trips__list">
				{rows.map(row => (
					<TripRow key={row.id} info={row} linked={linkTrips} />
				))}
			</div>
		</div>
	);
}

function TripRowSkeleton() {
	return (
		<div className="cancelled-trip-row" aria-hidden="true">
			<div className="cancelled-trip-row__accent" />
			<div className="cancelled-trip-row__body" style={{ flex: 1 }}>
				<Skeleton width="60%" height="14px" />
				<Skeleton width="40%" height="12px" />
			</div>
			<div className="cancelled-trip-row__meta">
				<Skeleton width="60px" height="11px" />
			</div>
		</div>
	);
}

interface TripInfo {
	id: string;
	humanReadableId?: string;
	startStopName?: string;
	endStopName?: string;
	eventName?: string;
	formattedDate: string | null;
	isMigrated: boolean;
}

function extractTripInfo(booking: CancelledBookingSummaryDto): TripInfo | null {
	// Stop names are resolved client-side from the ids the endpoint returns (stops are
	// reference data already loaded globally).
	const startStop = booking.startStopId ? getStopById(booking.startStopId) : undefined;
	const endStop = booking.endStopId ? getStopById(booking.endStopId) : undefined;
	const startStopName = startStop?.location?.name ?? startStop?.location?.shortName;
	const endStopName = endStop?.location?.name ?? endStop?.location?.shortName;
	const formattedDate = booking.departureDateTime
		? formatWeekdayDayMonthTime(booking.departureDateTime, true)
		: null;
	const hasAny =
		stringNotEmpty(booking.eventName) ||
		stringNotEmpty(startStopName) ||
		stringNotEmpty(endStopName) ||
		!!formattedDate ||
		stringNotEmpty(booking.humanReadableId);
	if (!hasAny) return null;
	return {
		id: booking.id,
		humanReadableId: booking.humanReadableId,
		startStopName: startStopName ?? undefined,
		endStopName: endStopName ?? undefined,
		eventName: stringNotEmpty(booking.eventName) ? booking.eventName : undefined,
		formattedDate,
		isMigrated: booking.isMigrated,
	};
}

function TripRow({ info, linked }: { info: TripInfo; linked: boolean }) {
	const hasEvent = stringNotEmpty(info.eventName);
	const hasRoute = !hasEvent && stringNotEmpty(info.startStopName) && stringNotEmpty(info.endStopName);
	// Booking detail route accepts either GUID or human-readable id — using the latter
	// keeps URLs friendlier and they're already uppercase in display.
	const target = info.humanReadableId
		? `/bookings/${encodeURIComponent(info.humanReadableId)}`
		: `/bookings/${info.id}`;

	const content = (
		<>
			<div className="cancelled-trip-row__accent" />
			<div className="cancelled-trip-row__body">
				{hasEvent && (
					<div className="cancelled-trip-row__event">
						<span>{info.eventName}</span>
					</div>
				)}
				{hasRoute && (
					<div className="cancelled-trip-row__route">
						<span>{info.startStopName}</span>
						<Icon name="arrow_forward" useMaterialIcons />
						<span>{info.endStopName}</span>
					</div>
				)}
				{info.formattedDate && (
					<div className="cancelled-trip-row__date">
						<Icon name="event" useMaterialIcons />
						<span>{info.formattedDate}</span>
					</div>
				)}
			</div>
			<div className="cancelled-trip-row__meta">
				{info.isMigrated && <span className="cancelled-trip-row__badge">Migrated</span>}
				{info.humanReadableId && (
					<span className="cancelled-trip-row__id">#{info.humanReadableId.toUpperCase()}</span>
				)}
				{linked && <Icon name="chevron_right" useMaterialIcons classname="cancelled-trip-row__chevron" />}
			</div>
		</>
	);

	if (!linked) {
		return <div className="cancelled-trip-row">{content}</div>;
	}

	return (
		<Link to={target} className="cancelled-trip-row cancelled-trip-row--linked">
			{content}
		</Link>
	);
}

// ─── Spiel: info icon + headline + body ─────────────────────────────────────

function CancelSpiel({ userName, isEventBooking }: { userName?: string; isEventBooking: boolean }) {
	const staffActingOnCustomer = store.isStaff && !!userName;
	const displayName = staffActingOnCustomer ? `${userName}'s` : 'Your';
	const subject = isEventBooking ? 'event booking' : 'trip';
	const headlineOnly = staffActingOnCustomer;
	return (
		<div className={`cancel-spiel${headlineOnly ? ' cancel-spiel--headline-only' : ''}`}>
			{staffActingOnCustomer && (
				<h2 className="cancel-spiel__headline">{`${displayName} ${subject} has been cancelled.`}</h2>
			)}
			{!staffActingOnCustomer && (
				<p className="cancel-spiel__body">
					<FormattedMessage
						id="booking-cancel-success__body"
						defaultMessage="Your request for cancellation has been successful. We hope to see you again soon."
					/>
				</p>
			)}
		</div>
	);
}

// ─── Resolution: refund or voucher card inside the main card ───────────────

interface ResolutionSectionProps {
	refunds?: RefundEntity[];
	giftCertificateUsage?: GiftCertificateUsageEntity;
	redeemText?: string;
	loading?: boolean;
	// Ferry bookings show "Ferry credit"; non-ferry (events) show "Credit".
	isFerryBooking?: boolean;
}

function ResolutionSection({
	refunds,
	giftCertificateUsage,
	redeemText,
	loading,
	isFerryBooking = true,
}: ResolutionSectionProps) {
	if (loading) {
		return (
			<div className="cancel-resolution">
				<ResolutionHeading label="Resolution" />
				<div className="cancel-resolution__rows">
					<ResolutionRowSkeleton />
				</div>
			</div>
		);
	}

	const hasRefunds = isNotNullOrUndefined(refunds) && refunds.length > 0;

	if (hasRefunds) {
		const manualRefunds = refunds!.filter(x => stringIsEmpty(x.providerRefundId));
		const providerRefunds = refunds!.filter(x => stringNotEmpty(x.providerRefundId));
		const manualAmount = manualRefunds.reduce((sum, r) => sum + (r.amount ?? 0), 0);
		const providerAmount = providerRefunds.reduce((sum, r) => sum + (r.amount ?? 0), 0);

		// Heading reflects what's actually inside — single card / single manual / mixed.
		let resolutionLabel = 'Refund';
		if (providerRefunds.length > 0 && manualRefunds.length === 0) resolutionLabel = 'Card refund';
		else if (manualRefunds.length > 0 && providerRefunds.length === 0) resolutionLabel = 'Manual refund';
		else resolutionLabel = 'Refund split';

		const cardEta = store.isStaff
			? "Allow 5–10 business days for the funds to reach the customer's account."
			: 'Allow 5–10 business days for the funds to reach your account.';
		const manualEta = store.isStaff
			? 'Settle this refund with the customer offline via your usual workflow.'
			: 'Staff will contact you to settle this refund offline.';

		return (
			<div className="cancel-resolution">
				<ResolutionHeading label={`${resolutionLabel}`} showCheck />
				<div className="cancel-resolution__rows">
					{providerRefunds.length > 0 && (
						<ResolutionRefundRow
							icon="credit_card"
							title="Direct refund"
							amount={providerAmount}
							subtitle={describeProviderRefund(providerRefunds)}
							body={cardEta}
						/>
					)}
					{manualRefunds.length > 0 && (
						<ResolutionRefundRow
							icon="person"
							title="Manual refund"
							amount={manualAmount}
							subtitle={null}
							body={manualEta}
						/>
					)}
				</div>
			</div>
		);
	}

	if (giftCertificateUsage?.giftCertificate) {
		const cert = giftCertificateUsage.giftCertificate;
		return (
			<div className="cancel-resolution">
				<ResolutionHeading label={`${creditTermLabel(isFerryBooking)} issued`} showCheck />
				<VoucherTicket
					isGift={false}
					code={cert.code ?? ''}
					amount={cert.initialValue ?? 0}
					status="active"
					expiryText={`Expires ${formatShortDateYear(cert.expiryDate)}`}
				/>
				{redeemText && (
					<>
						<div className="cancel-resolution__sub-heading">
							<ResolutionHeading label="How to redeem" />
						</div>
						<div
							className="cancel-resolution__redeem wysiwyg-content"
							dangerouslySetInnerHTML={{ __html: redeemText }}
						/>
					</>
				)}
			</div>
		);
	}

	return null;
}

function ResolutionHeading({ label, showCheck }: { label: string; showCheck?: boolean }) {
	return (
		<div className="cancel-resolution__heading">
			<span className="cancel-resolution__eyebrow">{label}</span>
			{showCheck && (
				<span className="cancel-resolution__check">
					<Icon name="check_circle" useMaterialIcons />
				</span>
			)}
		</div>
	);
}

interface ResolutionRefundRowProps {
	icon: string;
	title: string;
	amount: number;
	subtitle: string | null;
	body: string;
}

function ResolutionRowSkeleton() {
	return (
		<div className="cancel-resolution__row" aria-hidden="true">
			<div className="cancel-resolution__row-icon">
				<Skeleton width="20px" height="20px" borderRadius="50%" />
			</div>
			<div className="cancel-resolution__row-body" style={{ flex: 1 }}>
				<div className="cancel-resolution__row-top">
					<Skeleton width="40%" height="14px" />
					<Skeleton width="60px" height="14px" />
				</div>
				<Skeleton width="80%" height="12px" />
			</div>
		</div>
	);
}

function ResolutionRefundRow({ icon, title, amount, subtitle, body }: ResolutionRefundRowProps) {
	return (
		<div className="cancel-resolution__row">
			<div className="cancel-resolution__row-icon">
				<Icon name={icon} useMaterialIcons />
			</div>
			<div className="cancel-resolution__row-body">
				<div className="cancel-resolution__row-top">
					<span className="cancel-resolution__row-title">{title}</span>
					<span className="cancel-resolution__row-amount">{formatCurrency(amount)}</span>
				</div>
				{subtitle && <p className="cancel-resolution__row-subtitle">{subtitle}</p>}
				<p className="cancel-resolution__row-text">{body}</p>
			</div>
		</div>
	);
}

function describeProviderRefund(providerRefunds: RefundEntity[]): string | null {
	// Aggregate per-card so multi-card refunds show as "Visa ****1234, Mastercard ****4321"
	const seen = new Map<string, string>();
	for (const refund of providerRefunds) {
		const brand = refund.cardBrand ? upperCaseFirst(refund.cardBrand) : 'Card';
		const last4 = refund.last4 ? ` ****${refund.last4}` : '';
		const key = `${brand}${last4}`;
		if (!seen.has(key)) seen.set(key, key);
	}
	return seen.size > 0 ? Array.from(seen.values()).join(', ') : null;
}

async function fetchRefunds(transactionId: string) {
	const response = await axios.get<RefundEntity[]>(`/api/entity/RefundEntity/for-transaction/${transactionId}`);
	return (response.data ?? []).map(row => new RefundEntity(row));
}
